import { useState, useMemo, useEffect } from 'react';
import ReceptionFollowUps from '../components/ReceptionFollowUps';
import { useAppContext } from '../context/AppContext';
import TimeSelect from '../components/TimeSelect';
import { STAGE_LABELS, STAGE_COLOURS } from '../lib/sampleData';
import { getPermissions } from '../lib/userSystem';
import { buildDay } from '../components/DailySheetV2';
import { hasDietaryAnswer } from '../lib/eventCompleteness';
import { todayKey, toLocalDateKey, addDays, parseLocalDate } from '../lib/dateHelpers';
import {
  ChevronLeft, ChevronRight, CalendarDays, CalendarRange, Grid3x3, Printer, ChefHat,
  AlertTriangle, ClipboardList, UtensilsCrossed, Sparkles, BedDouble, Users, Plus,
  Edit2, Trash2, X, Heart, ArrowRight,
} from 'lucide-react';
import { fetchOccupancyRange, onOccupancyChanged, saveOccupancy, clearOccupancy, occWarning, formatOcc, enteredLabel } from '../lib/occupancy';
import OccupancyEditor from '../components/OccupancyEditor';
import { fetchVenueBlocksByDates, insertVenueBlock, deleteVenueBlock } from '../lib/supabaseService';

/* ═══════════════════════════════════════════════════════════════
   HOME (Oct 2026 redesign) — operations only, no finance.
   Day / Week / Year views with ◀ Today ▶ navigation.
   Day and Week use buildDay() — the same numbers as the printed
   function sheet and Kitchen Live. Year is the events board; clicking
   a date opens the venue add / block panel.
   In-house rooms/guests come from the weekly manual occupancy summary
   (hotel_occupancy_cache '_summary_' rows), so gaps show "Not entered".
   ═══════════════════════════════════════════════════════════════ */

const HIDDEN_STAGES = new Set(['archived', 'lost', 'cancelled']);
const CALENDAR_VENUE_IDS = ['skeff2', 'skeff1', 'foyer']; // Big Skeff, Small Skeff, Foyer
const WEDDING_COLOUR = '#E11D48';

const hhmm = (t) => (typeof t === 'string' ? t.slice(0, 5) : '');
const cleanName = (n) => String(n || '').replace(/\s*\(from table\)\s*$/i, '').trim();
const addKey = (k, n) => toLocalDateKey(addDays(k, n));
const fmtKey = (k, opts) => parseLocalDate(k).toLocaleDateString('en-IE', opts);
const isWedding = (e) => (e?.type || '').toLowerCase() === 'wedding';
// Any dietary answer counts (0s from 'No dietary requirements', NA, text).
const hasDiet = (e) => hasDietaryAnswer(e);
const mondayOf = (k) => addKey(k, -((parseLocalDate(k).getDay() + 6) % 7));

function Tile({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-bold uppercase tracking-wider">{icon}{label}</div>
      <div className="text-3xl font-bold text-gray-900 leading-tight mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard({ onNavigate, onSelectEvent }) {
  const {
    events = [], venues = [], currentUser, tableBookings = [], restaurantPackages = [],
    spaBookings = [], addSpaBooking, updateSpaBooking, deleteSpaBooking,
  } = useAppContext();
  const permissions = getPermissions(currentUser?.role);

  const today = todayKey();
  const [date, setDate] = useState(today);
  const [mode, setMode] = useState('day'); // day | week | year
  const year = +date.slice(0, 4);

  // Year board — venue add/block panel
  const [venueBlocks, setVenueBlocks] = useState([]);
  const [dayModal, setDayModal] = useState(null); // 'YYYY-MM-DD' | null
  const [blockDraft, setBlockDraft] = useState({});
  const [calendarBusy, setCalendarBusy] = useState(false);

  const data = { events, tableBookings, spaBookings, packages: restaurantPackages, venues };
  const day = useMemo(() => buildDay(date, data), [date, events, tableBookings, spaBookings, restaurantPackages, venues]); // eslint-disable-line react-hooks/exhaustive-deps
  const monday = mondayOf(date);
  const weekKeys = useMemo(() => Array.from({ length: 7 }, (_, i) => addKey(monday, i)), [monday]);
  const weekDays = useMemo(() => (mode === 'week' ? weekKeys.map((k) => ({ key: k, d: buildDay(k, data) })) : []),
    [mode, weekKeys, events, tableBookings, spaBookings, restaurantPackages, venues]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ IN-HOUSE (weekly manual summary) — fetched per year, ±1 week ═══
  const [occ, setOcc] = useState({});
  const [occReload, setOccReload] = useState(0);
  const [occEdit, setOccEdit] = useState(null); // date being edited from the Day tile
  useEffect(() => {
    let cancelled = false;
    fetchOccupancyRange(`${year - 1}-12-24`, `${year + 1}-01-07`)
      .then((m) => { if (!cancelled) setOcc(m); })
      .catch((err) => console.error('[Home] occupancy load failed', err));
    return () => { cancelled = true; };
  }, [year, occReload]);
  // A save anywhere (function sheet, editor) refreshes Home too.
  useEffect(() => onOccupancyChanged(() => setOccReload((x) => x + 1)), []);

  // ═══ YEAR BOARD — per-date event summary ═══
  const yearInfo = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      if (!e.date || HIDDEN_STAGES.has(e.stage)) return;
      const end = e.endDate && e.endDate > e.date ? e.endDate : e.date;
      for (let k = e.date, i = 0; k <= end && i < 31; k = addKey(k, 1), i++) {
        if (+k.slice(0, 4) !== year) continue;
        const x = m[k] || (m[k] = { n: 0, g: 0, w: 0, music: 0, top: null });
        if (e.type === 'music_night') { x.music += 1; continue; }
        x.n += 1; x.g += e.guests || 0; if (isWedding(e)) x.w += 1;
        if (!x.top || (e.guests || 0) > (x.top.guests || 0)) x.top = e;
      }
    });
    return m;
  }, [events, year]);

  useEffect(() => {
    if (mode !== 'year') return undefined;
    const dates = [];
    for (let k = `${year}-01-01`; k <= `${year}-12-31`; k = addKey(k, 1)) dates.push(k);
    let cancelled = false;
    fetchVenueBlocksByDates(dates)
      .then((rows) => { if (!cancelled) setVenueBlocks(rows); })
      .catch((err) => console.error('[Home] fetchVenueBlocksByDates failed:', err.message));
    return () => { cancelled = true; };
  }, [mode, year]);

  const calendarVenues = useMemo(() => CALENDAR_VENUE_IDS.map((id) => {
    const v = venues.find((x) => x.id === id);
    return { id, name: v?.name || id, colour: v?.colour || '#6B7280' };
  }), [venues]);

  // Venue rows for the add/block panel. A Full Skeff event/block covers
  // Big + Small Skeff and is shown as one row.
  const dayModalRows = useMemo(() => {
    if (!dayModal) return [];
    const expand = (vid) => (vid === 'skeff-full' ? ['skeff1', 'skeff2'] : [vid]);
    const byVenue = {};
    events.forEach((e) => {
      if (HIDDEN_STAGES.has(e.stage) || !e.date) return;
      const end = e.endDate && e.endDate > e.date ? e.endDate : e.date;
      if (dayModal < e.date || dayModal > end) return;
      expand(e.venue_id).forEach((vid) => { if (CALENDAR_VENUE_IDS.includes(vid) && !byVenue[vid]) byVenue[vid] = { type: 'event', event: e }; });
    });
    venueBlocks.forEach((b) => {
      if (b.date !== dayModal || b.sittingId !== 'all_day' || b.blockType !== 'closed') return;
      expand(b.venueId).forEach((vid) => { if (CALENDAR_VENUE_IDS.includes(vid) && !byVenue[vid]) byVenue[vid] = { type: 'blocked', block: b }; });
    });
    const rows = []; const seen = new Map();
    CALENDAR_VENUE_IDS.forEach((vid) => {
      const v = byVenue[vid] || { type: 'free' };
      if (v.type !== 'free') {
        const ref = v.event || v.block;
        if (seen.has(ref)) { seen.get(ref).venueIds.push(vid); return; }
        const entry = { ...v, venueId: vid, venueIds: [vid] }; seen.set(ref, entry); rows.push(entry); return;
      }
      rows.push({ ...v, venueId: vid, venueIds: [vid] });
    });
    return rows;
  }, [dayModal, events, venueBlocks]);

  const closeDayModal = () => { setDayModal(null); setBlockDraft({}); };
  const openEvent = (evt) => onSelectEvent?.(evt);
  const newEvent = (k, venueId = '') => onNavigate?.('events', { action: 'create-from-calendar', date: k, venue_id: venueId });

  const handleBlockDate = async (k, venueId) => {
    setCalendarBusy(true);
    try {
      await insertVenueBlock({ venueId, date: k, sittingId: 'all_day', reason: (blockDraft[venueId] || '').trim(), createdBy: currentUser?.name || '' });
      const fresh = await fetchVenueBlocksByDates([k]);
      setVenueBlocks((prev) => [...prev.filter((b) => !(b.date === k && b.venueId === venueId)), ...fresh.filter((b) => b.venueId === venueId)]);
      setBlockDraft((prev) => ({ ...prev, [venueId]: '' }));
    } catch (err) {
      alert('Could not block that date — please try again.');
      console.error('[Home] insertVenueBlock failed:', err.message);
    } finally { setCalendarBusy(false); }
  };
  const handleUnblockDate = async (block) => {
    setCalendarBusy(true);
    try {
      await deleteVenueBlock(block.id);
      setVenueBlocks((prev) => prev.filter((b) => b.id !== block.id));
    } catch (err) {
      alert('Could not remove that block — please try again.');
      console.error('[Home] deleteVenueBlock failed:', err.message);
    } finally { setCalendarBusy(false); }
  };

  // ═══ NAVIGATION ═══
  const step = (dir) => {
    if (mode === 'year') {
      const y = year + dir; const md = date.slice(5);
      setDate(md === '02-29' ? `${y}-02-28` : `${y}-${md}`);
    } else setDate(addKey(date, dir * (mode === 'week' ? 7 : 1)));
  };
  const isCurrent = mode === 'day' ? date === today : mode === 'week' ? monday === mondayOf(today) : year === +today.slice(0, 4);
  const rel = date === today ? 'Today' : date === addKey(today, 1) ? 'Tomorrow' : date === addKey(today, -1) ? 'Yesterday' : null;
  const now = new Date();

  // ═══ DAY VIEW DATA ═══
  const attention = useMemo(() => {
    const out = [];
    if (day.teaTomorrow.length) {
      out.push({ key: 'tea', red: true, text: `Order afternoon tea for ${fmtKey(addKey(date, 1), { weekday: 'long' })}: ${day.teaTomorrow.reduce((s, b) => s + (b.covers || 0), 0)} (${day.teaTomorrow.map((b) => `${hhmm(b.time)} ${cleanName(b.guestName)}`).join(', ')})` });
    }
    day.dayEvents.filter((e) => e.foodServiceTime !== 'none' && (e.guests || 0) > 0 && !hasDiet(e))
      .forEach((e) => out.push({ key: `d${e.id}`, text: `No dietary info yet: ${cleanName(e.name)} (${e.guests} guests)`, evt: e }));
    return out;
  }, [day, date]);

  const byArea = useMemo(() => {
    const m = {};
    day.dayTables.filter((b) => !b.isEvent).forEach((b) => {
      const a = day.area(b.venue);
      m[a] = m[a] || { covers: 0, bookings: 0, times: {} };
      m[a].covers += b.covers || 0; m[a].bookings += 1;
      const t = hhmm(b.time); m[a].times[t] = (m[a].times[t] || 0) + (b.covers || 0);
    });
    return Object.entries(m);
  }, [day]);

  const dayOcc = occ[date];

  const btn = 'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold';
  const seg = (m) => `flex items-center gap-1 px-3 py-2 text-sm font-semibold ${mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`;

  return (
    <div className="bg-gray-50 min-h-screen p-4 md:p-6 lg:p-8">
      {occEdit && <OccupancyEditor date={occEdit} current={occ[occEdit] || null} onClose={() => setOccEdit(null)} />}
      {/* YEAR BOARD — venue panel for a date: view / add / block */}
      {dayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeDayModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h3 className="text-base font-bold text-gray-900">{fmtKey(dayModal, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
              <button onClick={closeDayModal} className="p-1 hover:bg-gray-100 rounded h-8 w-8 flex items-center justify-center"><X size={18} /></button>
            </div>
            <button onClick={() => { setDate(dayModal); setMode('day'); closeDayModal(); }}
              className="w-full mb-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg py-2">
              Open this day <ArrowRight size={14} />
            </button>
            <div className="space-y-3">
              {dayModalRows.map((v) => {
                const names = v.venueIds.map((vid) => calendarVenues.find((cv) => cv.id === vid)?.name).filter(Boolean).join(' + ');
                return (
                  <div key={v.venueId} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      {v.venueIds.map((vid) => <span key={vid} className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: calendarVenues.find((cv) => cv.id === vid)?.colour }} />)}
                      <span className="text-sm font-bold text-gray-900">{names}</span>
                    </div>
                    {v.type === 'event' && (
                      <button onClick={() => { closeDayModal(); openEvent(v.event); }}
                        className={`w-full text-left flex items-center justify-between gap-2 p-2 rounded-md hover:bg-gray-50 border ${isWedding(v.event) ? 'border-rose-200 bg-rose-50/60' : 'border-gray-100'}`}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          {isWedding(v.event) && <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: WEDDING_COLOUR }}><Heart size={9} className="fill-white text-white" /></span>}
                          <span className="text-sm font-medium text-gray-900 truncate">{v.event.name || v.event.client || 'Event'}</span>
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded text-white whitespace-nowrap flex-shrink-0" style={{ backgroundColor: STAGE_COLOURS[v.event.stage] }}>{STAGE_LABELS[v.event.stage] || v.event.stage}</span>
                      </button>
                    )}
                    {v.type === 'blocked' && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-600">Blocked{v.block.reason ? ` — ${v.block.reason}` : ''}</span>
                        {permissions.canEditSettings && (
                          <button disabled={calendarBusy} onClick={() => handleUnblockDate(v.block)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium disabled:opacity-50">Unblock</button>
                        )}
                      </div>
                    )}
                    {v.type === 'free' && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-400">Free</span>
                          <button onClick={() => { const k = dayModal; closeDayModal(); newEvent(k, v.venueId); }} className="text-xs px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center gap-1"><Plus size={12} /> Add event</button>
                        </div>
                        {permissions.canEditSettings && (
                          <div className="flex items-center gap-2">
                            <input type="text" value={blockDraft[v.venueId] || ''} onChange={(e) => setBlockDraft((p) => ({ ...p, [v.venueId]: e.target.value }))}
                              placeholder="Reason (optional)" className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-400" />
                            <button disabled={calendarBusy} onClick={() => handleBlockDate(dayModal, v.venueId)} className="text-xs px-2.5 py-1.5 rounded bg-gray-700 hover:bg-gray-800 text-white font-medium disabled:opacity-50">Block</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* HEADER + NAVIGATION */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-sm text-gray-500">Good {now.getHours() < 12 ? 'morning' : now.getHours() < 17 ? 'afternoon' : 'evening'}, {currentUser?.name || 'there'}</div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {mode === 'year' ? `${year} events board`
                : mode === 'week' ? `Week of ${fmtKey(weekKeys[0], { day: 'numeric', month: 'short' })} – ${fmtKey(weekKeys[6], { day: 'numeric', month: 'short' })}`
                  : <>{rel && <span className="text-emerald-700">{rel} · </span>}{fmtKey(date, { weekday: 'long', day: 'numeric', month: 'long' })}</>}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <button onClick={() => step(-1)} className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100" aria-label="Previous"><ChevronLeft size={18} /></button>
              <button onClick={() => setDate(today)} className={`px-3 py-2 rounded-lg text-sm font-semibold border ${isCurrent ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-200 hover:bg-gray-100'}`}>Today</button>
              <button onClick={() => step(1)} className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100" aria-label="Next"><ChevronRight size={18} /></button>
            </div>
            <div className="flex rounded-lg overflow-hidden border border-gray-200">
              <button onClick={() => setMode('day')} className={seg('day')}><CalendarDays size={15} />Day</button>
              <button onClick={() => setMode('week')} className={seg('week')}><CalendarRange size={15} />Week</button>
              <button onClick={() => setMode('year')} className={seg('year')}><Grid3x3 size={15} />Year</button>
            </div>
            {mode !== 'year' && (
              <button onClick={() => onNavigate?.('functionsheets', { action: mode === 'day' ? 'print-daily' : 'print-weekly', date })}
                className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}><Printer size={15} />Print {mode === 'day' ? 'day' : 'week'}</button>
            )}
            <button onClick={() => onNavigate?.('kitchen')} className={`${btn} bg-gray-900 hover:bg-black text-white`}><ChefHat size={15} />Kitchen Live</button>
          </div>
        </div>

        <ReceptionFollowUps onSelectEvent={openEvent} />

        {mode === 'day' && (<>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Tile icon={<UtensilsCrossed size={12} />} label="Food covers" value={day.eventCovers + day.tableCovers} sub="events + restaurant" />
            <Tile icon={<ClipboardList size={12} />} label="Events" value={day.dayEvents.length} sub={`${day.eventCovers} guests`} />
            <Tile icon={<Users size={12} />} label="Restaurant" value={day.tableCovers} sub={`${day.tableCount} bookings`} />
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-bold uppercase tracking-wider"><BedDouble size={12} />In-house tonight</div>
              <button type="button" onClick={() => setOccEdit(date)} className="block w-full text-left" title="Tap to enter or change tonight's figures">
                {dayOcc ? (
                  <div className="flex items-end gap-4 mt-1">
                    <div><div className="text-3xl font-bold text-gray-900 leading-tight">{dayOcc.rooms}</div><div className="text-xs text-gray-500">rooms</div></div>
                    <div><div className="text-3xl font-bold text-gray-900 leading-tight">{dayOcc.guests}</div><div className="text-xs text-gray-500">guests</div></div>
                  </div>
                ) : <div className="text-sm text-amber-700 font-semibold mt-2">Not entered</div>}
                <div className="text-[11px] text-sky-700 font-semibold mt-0.5">{dayOcc ? 'Tap to edit' : '+ Tap to add'}{dayOcc && enteredLabel(dayOcc) ? <span className="text-gray-400 font-normal"> · {enteredLabel(dayOcc)}</span> : null}</div>
              </button>
            </div>
            <Tile icon={<Sparkles size={12} />} label="Spa" value={day.spaGuests} sub={`${day.daySpa.length} bookings`} />
          </div>

          {attention.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4 space-y-1">
              {attention.map((a) => (
                <button key={a.key} type="button" onClick={() => a.evt && openEvent(a.evt)}
                  className={`w-full text-left flex items-start gap-2 text-sm ${a.red ? 'text-red-700 font-semibold' : 'text-amber-800'} ${a.evt ? 'hover:underline' : 'cursor-default'}`}>
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />{a.text}
                </button>
              ))}
            </div>
          )}

          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 self-start">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="font-bold text-gray-900 flex items-center gap-2"><ClipboardList size={16} className="text-emerald-600" />Events</span>
                <button onClick={() => newEvent(date)} className="text-sm text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1"><Plus size={14} />New event</button>
              </div>
              {day.dayEvents.length === 0 && <p className="px-4 py-4 text-sm text-gray-400">No events.</p>}
              {day.musicEvents.length > 0 && (
                <p className="px-4 py-2 text-xs text-violet-700 border-b border-gray-50">♪ Music only:{' '}
                  {day.musicEvents.map((e, i) => (
                    <button key={e.id} type="button" onClick={() => openEvent(e)} className="hover:underline">{i > 0 ? ' · ' : ''}{hhmm(e.startTime)} {cleanName(e.name)}</button>
                  ))}
                </p>
              )}
              {day.dayEvents.map((e) => (
                <button key={e.id} type="button" onClick={() => openEvent(e)} className="w-full text-left px-4 py-2.5 border-b border-gray-50 last:border-b-0 flex items-center gap-3 hover:bg-gray-50">
                  <div className="w-28 shrink-0 text-sm font-bold tabular-nums text-gray-900 whitespace-nowrap">{hhmm(e.startTime)}{e.endTime ? `–${hhmm(e.endTime)}` : ''}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{isWedding(e) ? '💒 ' : ''}{cleanName(e.name)}</div>
                    <div className="text-xs text-gray-500">{day.venueName(e.venue_id)}{e.foodServiceTime === 'none' ? ' · drinks only' : ''}</div>
                    {e.tickConflicts && <div className="text-xs font-semibold text-amber-700">⚠ {e.tickConflicts.join('; ')}</div>}
                  </div>
                  {e.foodServiceTime !== 'none' && !hasDiet(e) && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 shrink-0">no dietary</span>}
                  <div className="text-lg font-bold text-gray-900 tabular-nums w-12 text-right shrink-0">{e.guests || 0}</div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                  <span className="font-bold text-gray-900 flex items-center gap-2"><UtensilsCrossed size={16} className="text-orange-500" />Restaurant &amp; bar</span>
                  <button onClick={() => onNavigate?.('tables', { action: 'create-for-date', date })} className="text-sm text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1"><Plus size={14} />New booking</button>
                </div>
                {byArea.length === 0 && <p className="px-4 py-4 text-sm text-gray-400">No table bookings.</p>}
                {byArea.map(([a, v]) => {
                  const peak = Object.entries(v.times).sort((x, y) => y[1] - x[1])[0];
                  return (
                    <button key={a} type="button" onClick={() => onNavigate?.('tables')} className="w-full text-left px-4 py-2 border-b border-gray-50 last:border-b-0 flex items-center justify-between hover:bg-gray-50">
                      <div><div className="font-semibold text-gray-900">{a}</div><div className="text-xs text-gray-500">{v.bookings} booking{v.bookings !== 1 ? 's' : ''}{peak && peak[0] ? ` · busiest ${peak[0]}` : ''}</div></div>
                      <div className="text-lg font-bold text-gray-900">{v.covers}</div>
                    </button>
                  );
                })}
                {day.teaToday.length > 0 && <div className="px-4 py-2 text-sm text-pink-700 font-semibold">Afternoon tea: {day.teaToday.reduce((s, b) => s + (b.covers || 0), 0)}</div>}
              </div>

              <SpaCard key={date} date={date} spaBookings={spaBookings} addSpaBooking={addSpaBooking} updateSpaBooking={updateSpaBooking} deleteSpaBooking={deleteSpaBooking} />
            </div>
          </div>
        </>)}

        {mode === 'week' && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {weekDays.map(({ key: k, d: w }) => {
              const o = occ[k];
              return (
                <button key={k} type="button" onClick={() => { setDate(k); setMode('day'); }}
                  className={`text-left flex flex-col rounded-xl border p-3 bg-white hover:border-emerald-500 ${k === today ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-gray-200'} ${k < today ? 'opacity-50' : ''}`}>
                  <div className="text-[11px] font-bold uppercase text-emerald-700 h-4">{k === today ? 'Today' : k === addKey(today, 1) ? 'Tomorrow' : ''}</div>
                  <div className="font-bold text-gray-900">{fmtKey(k, { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  <div className="text-3xl font-bold text-gray-900 leading-tight">{w.eventCovers + w.tableCovers}</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">food covers</div>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <div>{w.dayEvents.length} events · {w.eventCovers}</div>
                    <div>{w.tableCount} tables · {w.tableCovers}</div>
                    <div className="font-semibold text-sky-800">🛏 {formatOcc(o || null)}</div>
                    <div>Spa {w.spaGuests}</div>
                  </div>
                  {w.dayEvents.slice(0, 3).map((e) => <div key={e.id} className="mt-1.5 text-xs text-gray-800 leading-snug"><b>{hhmm(e.startTime)}</b> {cleanName(e.name)}</div>)}
                  {w.dayEvents.length > 3 && <div className="text-xs text-gray-400">+{w.dayEvents.length - 3} more</div>}
                  {w.musicEvents.length > 0 && <div className="mt-1 text-[11px] text-violet-700">♪ {w.musicEvents.length} music</div>}
                </button>
              );
            })}
            <div className="col-span-full text-sm text-gray-400">Tap a day to open it.</div>
            <div className="col-span-full">
              <OccupancyWeekEntry weekKeys={weekKeys} occ={occ} onSaved={() => setOccReload((x) => x + 1)} />
            </div>
          </div>
        )}

        {mode === 'year' && (
          <div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 mb-3">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-200" />Under 50 guests</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-300" />50–149</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-600" />150+</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-pink-500" />Wedding</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />Music night</span>
              <span className="text-gray-400">Hover a day for details · click to view, add or block</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Array.from({ length: 12 }, (_, mi) => {
                const first = new Date(year, mi, 1);
                const lead = (first.getDay() + 6) % 7;
                const days = new Date(year, mi + 1, 0).getDate();
                const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => toLocalDateKey(new Date(year, mi, i + 1)))];
                const monthEvents = cells.reduce((s, k) => s + (k && yearInfo[k] ? yearInfo[k].n : 0), 0);
                const monthWeddings = cells.reduce((s, k) => s + (k && yearInfo[k] ? yearInfo[k].w : 0), 0);
                return (
                  <div key={mi} className="bg-white rounded-xl border border-gray-200 p-3">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-bold text-gray-900">{first.toLocaleDateString('en-IE', { month: 'long' })}</span>
                      <span className="text-[11px] text-gray-500">{monthEvents} events{monthWeddings ? ` · ${monthWeddings} 💒` : ''}</span>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5 text-[9px] text-gray-400 mb-0.5">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((l, i) => <div key={i} className="text-center">{l}</div>)}</div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {cells.map((k, i) => {
                        if (!k) return <div key={i} />;
                        const info = yearInfo[k];
                        const g = info ? info.g : 0;
                        const o = occ[k];
                        const bg = !info || info.n === 0 ? 'bg-gray-50 text-gray-500' : info.w ? 'bg-pink-500 text-white' : g >= 150 ? 'bg-emerald-600 text-white' : g >= 50 ? 'bg-emerald-300 text-emerald-950' : 'bg-emerald-100 text-emerald-900';
                        const tip = `${fmtKey(k, { weekday: 'short', day: 'numeric', month: 'short' })}${info && info.n ? ` · ${info.n} event${info.n !== 1 ? 's' : ''}, ${g} guests — ${cleanName(info.top?.name)}` : ' · no events'}${info && info.music ? ' · music night' : ''}${o ? ` · In-house: ${o.rooms} rooms, ${o.guests} guests` : ''}`;
                        return (
                          <button key={k} type="button" title={tip} onClick={() => setDayModal(k)}
                            className={`relative h-7 rounded text-[11px] font-semibold ${bg} ${k === today ? 'ring-2 ring-offset-1 ring-gray-900' : ''} ${k < today && (!info || !info.n) ? 'opacity-50' : ''} hover:ring-2 hover:ring-emerald-500`}>
                            {+k.slice(8)}
                            {info && info.music ? <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-indigo-500" /> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   IN-HOUSE ENTRY — rooms / guests for the 7 days on screen, as a small
   labelled table (Rooms row, Guests row) so the two can't be mixed up.
   Only changed days are saved; blanking a day clears it. A day where the
   numbers look wrong (fewer guests than rooms, etc.) is flagged.
   ═══════════════════════════════════════════════════════════════ */
function OccupancyWeekEntry({ weekKeys, occ, onSaved }) {
  const initial = () => Object.fromEntries(weekKeys.map((k) => [k, { rooms: occ[k] ? String(occ[k].rooms) : '', guests: occ[k] ? String(occ[k].guests) : '' }]));
  const [vals, setVals] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const sig = weekKeys.map((k) => `${k}:${occ[k] ? `${occ[k].rooms}/${occ[k].guests}` : ''}`).join('|');
  useEffect(() => { setVals(initial()); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = weekKeys.filter((k) => {
    const v = vals[k] || { rooms: '', guests: '' };
    const o = occ[k];
    if (v.rooms === '' && v.guests === '') return !!o; // blanked → clear it
    return !o || String(o.rooms) !== v.rooms || String(o.guests) !== v.guests;
  });
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      for (const k of changed) {
        const v = vals[k];
        if (v.rooms === '' && v.guests === '') await clearOccupancy(k);
        else await saveOccupancy(k, v.rooms, v.guests);
      }
      setMsg(`Saved ${changed.length} day${changed.length !== 1 ? 's' : ''}`);
      onSaved?.();
    } catch (err) {
      console.error('[Home] save occupancy failed:', err);
      setMsg('Could not save — please try again');
    } finally { setSaving(false); }
  };
  const set = (k, f, v) => { setMsg(''); setVals((p) => ({ ...p, [k]: { ...(p[k] || { rooms: '', guests: '' }), [f]: v.replace(/[^0-9]/g, '').slice(0, 3) } })); };
  const input = (dirty) => `w-full px-1.5 py-1.5 border rounded text-base font-semibold text-center tabular-nums focus:outline-none focus:border-sky-600 ${dirty ? 'border-sky-500 bg-sky-50' : 'border-gray-300'}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 mt-1">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="font-bold text-gray-900 text-sm flex items-center gap-1.5"><BedDouble size={15} className="text-sky-700" />In-house figures for this week</span>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-gray-500">{msg}</span>}
          <button onClick={save} disabled={saving || changed.length === 0} className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-40">{saving ? 'Saving…' : changed.length ? `Save ${changed.length} day${changed.length !== 1 ? 's' : ''}` : 'Save'}</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] border-separate border-spacing-1">
          <thead>
            <tr>
              <th className="w-16" />
              {weekKeys.map((k) => <th key={k} className="text-[11px] font-semibold text-gray-500 text-center">{fmtKey(k, { weekday: 'short', day: 'numeric' })}</th>)}
            </tr>
          </thead>
          <tbody>
            {[['rooms', 'Rooms'], ['guests', 'Guests']].map(([f, lbl]) => (
              <tr key={f}>
                <th scope="row" className="text-left text-xs font-bold uppercase tracking-wide text-gray-600 pr-1">{lbl}</th>
                {weekKeys.map((k) => (
                  <td key={k}>
                    <input inputMode="numeric" aria-label={`${lbl} ${k}`} value={vals[k]?.[f] ?? ''} onChange={(e) => set(k, f, e.target.value)} className={input(changed.includes(k))} />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td />
              {weekKeys.map((k) => {
                const w = occWarning(vals[k]?.rooms, vals[k]?.guests);
                return <td key={k} className="align-top">{w ? <div className="text-[10px] leading-tight text-amber-700 font-semibold" title={w}>⚠ {w.split(' — ')[0]}</div> : null}</td>;
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Clear both boxes and save to remove a day. Changes show on function sheets and Kitchen straight away.</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SPA — compact list for the day being viewed, with add / edit /
   delete. Reception enters spa on the day; it flows straight into
   the function sheet and Kitchen Live (spa brunch slots).
   ═══════════════════════════════════════════════════════════════ */
function SpaCard({ date, spaBookings, addSpaBooking, updateSpaBooking, deleteSpaBooking }) {
  const emptyForm = { date, time: '', guestName: '', details: '', covers: '', deposit: '', comments: '' };
  const [draft, setDraft] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const list = (spaBookings || []).filter((b) => b.date === date).sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  const openAdd = () => { setEditingId(null); setDraft({ ...emptyForm, date }); setShowForm(true); };
  const startEdit = (b) => {
    setEditingId(b.id);
    setDraft({ date: b.date || date, time: b.time || '', guestName: b.guestName || '', details: b.details || '', covers: b.covers ?? '', deposit: b.deposit ?? '', comments: b.comments || '' });
    setShowForm(true);
  };
  const cancel = () => { setEditingId(null); setDraft({ ...emptyForm, date }); setShowForm(false); };
  const save = async () => {
    const payload = {
      date: draft.date || date,
      time: (draft.time || '').trim(),
      guestName: (draft.guestName || '').trim(),
      details: (draft.details || '').trim(),
      covers: draft.covers === '' ? null : Number(draft.covers),
      deposit: draft.deposit === '' ? null : Number(draft.deposit),
      comments: (draft.comments || '').trim(),
    };
    if (!payload.guestName && !payload.time && !payload.details) return;
    try {
      if (editingId) await updateSpaBooking(editingId, payload); else await addSpaBooking(payload);
      cancel();
    } catch (err) {
      console.error('Spa booking save failed:', err);
      alert('Could not save spa booking — please try again.');
    }
  };
  const remove = async (id) => { if (window.confirm('Delete this spa booking?')) await deleteSpaBooking(id); };

  const input = 'w-full px-2 py-1.5 border border-gray-300 rounded text-sm';
  const label = 'block text-[11px] font-semibold text-gray-500 uppercase mb-0.5';

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <span className="font-bold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-purple-500" />Spa</span>
        {!showForm && <button onClick={openAdd} className="text-sm text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1"><Plus size={14} />Add</button>}
      </div>

      {showForm && (
        <div className="p-3 border-b border-gray-100 bg-gray-50">
          <div className="grid grid-cols-2 gap-2">
            <div><label className={label}>Date</label><input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className={input} /></div>
            <div><label className={label}>Time</label>
              <TimeSelect value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} startHour={7} endHour={21} stepMinutes={15} placeholder="-- time --" className={input} />
            </div>
            <div className="col-span-2"><label className={label}>Guest name</label><input type="text" value={draft.guestName} onChange={(e) => setDraft({ ...draft, guestName: e.target.value })} className={input} placeholder="Mary Smith" /></div>
            <div className="col-span-2"><label className={label}>Details</label><input type="text" value={draft.details} onChange={(e) => setDraft({ ...draft, details: e.target.value })} className={input} placeholder="Spa & brunch, massage 60min…" /></div>
            <div><label className={label}>Guests</label><input type="number" min="0" value={draft.covers} onChange={(e) => setDraft({ ...draft, covers: e.target.value })} className={input} placeholder="1" /></div>
            <div><label className={label}>Deposit (€)</label><input type="number" min="0" step="0.01" value={draft.deposit} onChange={(e) => setDraft({ ...draft, deposit: e.target.value })} className={input} /></div>
            <div className="col-span-2"><label className={label}>Comments</label><input type="text" value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} className={input} placeholder="Therapist, room, allergies…" /></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={save} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">{editingId ? 'Save' : 'Add booking'}</button>
            <button onClick={cancel} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium">Cancel</button>
          </div>
        </div>
      )}

      {list.length === 0 && !showForm && <p className="px-4 py-4 text-sm text-gray-400">No spa bookings entered.</p>}
      {list.map((b) => (
        <div key={b.id} className="px-4 py-1.5 border-b border-gray-50 last:border-b-0 flex items-center gap-2 text-sm group">
          <span className="font-bold tabular-nums w-12 shrink-0">{hhmm(b.time) || '--:--'}</span>
          <span className="flex-1 min-w-0 truncate" title={[b.details, b.comments].filter(Boolean).join(' · ')}>
            <span className="font-medium text-gray-900">{b.guestName || '—'}</span>
            {b.details && <span className="text-gray-500"> · {b.details}</span>}
          </span>
          <span className="font-semibold w-6 text-right shrink-0">{b.covers ?? ''}</span>
          <button onClick={() => startEdit(b)} className="p-1 hover:bg-gray-100 rounded text-blue-600 shrink-0" title="Edit"><Edit2 size={13} /></button>
          <button onClick={() => remove(b.id)} className="p-1 hover:bg-gray-100 rounded text-red-600 shrink-0" title="Delete"><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
}
